import { Pencil, Plus, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';

import type { TargetKind, TargetOption } from '../schemas/target-kinds';

import { TargetLabel } from './target-label';
import { TargetPicker } from './target-picker';

export interface TargetIdFieldProps {
  storeId: string;
  kind: TargetKind;
  value: string | null;
  /** Receives the picked option so a caller can reuse its title; null clears. */
  onChange: (option: TargetOption | null) => void;
  /** Singular noun, e.g. "collection". */
  label: string;
  clearable?: boolean;
  invalid?: boolean;
}

/** One store record id, picked through a search dialog that closes on the first pick. */
export function TargetIdField({
  storeId,
  kind,
  value,
  onChange,
  label,
  clearable = true,
  invalid = false,
}: TargetIdFieldProps) {
  const { t } = useTranslation('linkTargets');
  const [open, setOpen] = useState(false);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div
        className={
          invalid
            ? 'border-destructive min-w-0 flex-1 rounded-md border px-3 py-1.5 text-sm'
            : 'min-w-0 flex-1 rounded-md border px-3 py-1.5 text-sm'
        }
      >
        {value ? (
          <TargetLabel storeId={storeId} kind={kind} id={value} showPath />
        ) : (
          <span className="text-muted-foreground">{t('id.none', { label })}</span>
        )}
      </div>
      <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)}>
        {value ? <Pencil aria-hidden /> : <Plus aria-hidden />}
        {value ? t('id.change', { label }) : t('id.choose', { label })}
      </Button>
      {value && clearable ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label={t('id.clear', { label })}
          onClick={() => onChange(null)}
        >
          <X aria-hidden />
        </Button>
      ) : null}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('id.dialogTitle', { label })}</DialogTitle>
            <DialogDescription>{t('id.dialogDescription')}</DialogDescription>
          </DialogHeader>
          <TargetPicker
            storeId={storeId}
            kind={kind}
            selectedIds={new Set(value ? [value] : [])}
            onPick={(option) => {
              onChange(option);
              setOpen(false);
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
